import { Link } from 'react-router';
import { useState, type ReactNode } from 'react';
import type { OutputValue } from '@batixa/engine';
import { ApiError } from '../lib/api';

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  let message = error instanceof Error ? error.message : 'Erreur inattendue';
  if (error instanceof ApiError && Array.isArray(error.details) && error.details.length) {
    message += ' — ' + (error.details as Array<{ message: string; output?: string; path?: string }>).map((d) => (d.output ? `${d.output} : ${d.message}` : d.message)).join(' · ');
  }
  const paywall = error instanceof ApiError && error.code === 'DOWNLOAD_PAYMENT';
  return (
    <div className="rounded-lg border border-bad/30 bg-bad/5 px-3 py-2 text-sm text-bad">
      {message}
      {paywall && <> <Link to="/parametres/abonnement/payer/telechargement" className="font-semibold underline">Payer le téléchargement</Link></>}
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </label>
  );
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'ok' | 'warn' | 'bad' | 'info'; children: ReactNode }) {
  const tones = {
    neutral: 'bg-surface text-muted',
    ok: 'bg-ok/10 text-ok',
    warn: 'bg-warn/10 text-warn',
    bad: 'bg-bad/10 text-bad',
    info: 'bg-tech-50 text-tech-600',
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${tones[tone]}`}>{children}</span>;
}

/** « Comment cette quantité a-t-elle été calculée ? » */
export function OutputRow({ output }: { output: OutputValue }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line last:border-0">
      <button className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-surface" onClick={() => setOpen((o) => !o)}>
        <span className={output.primary ? 'font-semibold' : ''}>
          {output.error ? '🔴' : '🟢'} {output.label}
        </span>
        <span className="flex items-center gap-3">
          <span className={`tabular-nums ${output.error ? 'text-bad' : 'font-semibold text-navy-900'}`}>{output.error ? 'donnée manquante' : output.display}</span>
          <span className="text-xs text-tech-600">{open ? 'Masquer' : 'Voir le calcul'}</span>
        </span>
      </button>
      {open && (
        <div className="mx-3 mb-3 rounded-lg bg-navy-900 p-3 font-mono text-xs text-white/85">
          {output.error ? (
            <div className="text-brand-500">{output.error.message}</div>
          ) : (
            output.steps?.map((s, i) => (
              <div key={i} className={i === output.steps!.length - 1 ? 'mt-1 font-bold text-brand-500' : ''}>
                {s}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: Array<{ value: T; label: string }> }) {
  return (
    <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
      {items.map((i) => (
        <button
          key={i.value}
          onClick={() => onChange(i.value)}
          className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2 text-sm font-semibold transition ${value === i.value ? 'border-brand-500 text-navy-900' : 'border-transparent text-muted hover:text-ink'}`}
        >
          {i.label}
        </button>
      ))}
    </div>
  );
}
